"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Close } from "@/components/ui/icons";
import { useLenis } from "@/hooks/useLenis";
import { Confetti } from "./Confetti";
import { DemoSuccessPanel } from "./DemoSuccessPanel";

const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * The confirmation that opens over the form once a demo request is in — with the popper.
 *
 * Rendered into `document.body` rather than in place: the form card is transformed by its
 * reveal, and a transformed ancestor would make `fixed` resolve against the card instead
 * of the viewport.
 */
export function DemoSuccessDialog({ name, onClose }: { name?: string; onClose: () => void }) {
  const [mounted, setMounted] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lenis = useLenis();

  useEffect(() => setMounted(true), []);

  // Waits for the portal: on the first pass nothing is rendered yet, so there is no button to focus.
  useEffect(() => {
    if (!mounted) return;
    closeRef.current?.focus();
    lenis?.stop();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenis?.start();
    };
  }, [mounted, lenis, onClose]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[60] grid place-items-center overflow-y-auto overscroll-contain px-5 py-10"
        data-lenis-prevent=""
        // Only a click that starts and ends on the backdrop itself dismisses, so a drag
        // that happens to finish outside the card does not throw the dialog away.
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 -z-10 bg-ink/50 backdrop-blur-[3px]"
        />

        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-success-title"
          initial={{ opacity: 0, y: 22, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.98 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="relative w-full max-w-[540px] rounded-[26px] bg-white p-6 text-ink shadow-[0_40px_90px_-30px_rgba(10,10,11,0.45)] md:p-9"
        >
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-panel hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Close className="size-4" />
          </button>

          <DemoSuccessPanel name={name} />
        </motion.div>
      </div>
      <Confetti />
    </>,
    document.body,
  );
}
